import { createRoute, redirect, type AnyRoute } from '@tanstack/react-router';
import { AdminLayout } from '@/features/admin/AdminLayout';
import { CAMINHOS, RAIZ_DA_ADMINISTRACAO } from '@/features/admin/navegacao';
import { NotificationsPage } from '@/features/admin/NotificationsPage';
import { OrganizationsPage } from '@/features/admin/OrganizationsPage';
import { SlaPage } from '@/features/admin/SlaPage';
import { TeamsPage } from '@/features/admin/TeamsPage';
import { TicketCategoriesPage } from '@/features/admin/TicketCategoriesPage';
import { TicketStatusesPage } from '@/features/admin/TicketStatusesPage';
import { TicketSubcategoriesPage } from '@/features/admin/TicketSubcategoriesPage';
import { EmailRoutingPage } from '@/features/admin/EmailRoutingPage';
import { TicketFieldDefinitionsPage } from '@/features/admin/TicketFieldDefinitionsPage';
import { UsuariosPage } from '@/features/admin/UsuariosPage';

/**
 * Rotas da administração.
 *
 * Exportadas como **fábrica**, e não como objetos prontos, pelo mesmo motivo de
 * `features/tickets/routes.ts`: `createRoute` exige a rota pai, e o pai
 * (`appRoute`, com o `AppShell` e a guarda de sessão) mora em
 * `app/router.tsx`, que pertence a outro dono. Receber o pai por parâmetro
 * deixa a integração ser uma linha lá, sem que esta feature importe de dentro
 * de `app/` — o que fecharia um ciclo entre o roteador e a tela que ele monta.
 *
 * A diferença para a fábrica de chamados é que aqui volta **uma rota só**, com
 * os filhos já pendurados. As cinco abas são detalhe interno da administração:
 * quem monta o roteador não precisa saber que existem cinco nem em que ordem,
 * e listá-las lá obrigaria a mexer em `app/router.tsx` a cada catálogo novo.
 *
 * Integração esperada em `app/router.tsx`:
 *
 * ```ts
 * const administracaoRoute = criarRotasDeAdministracao(appRoute);
 * appRoute.addChildren([indexRoute, administracaoRoute]);
 * ```
 */
export function criarRotasDeAdministracao<TRotaPai extends AnyRoute>(rotaPai: TRotaPai) {
  const raiz = createRoute({
    getParentRoute: () => rotaPai,
    path: RAIZ_DA_ADMINISTRACAO,
    component: AdminLayout,
  });

  /**
   * `/administracao` sozinho não é tela: redireciona para a primeira aba.
   *
   * A alternativa — repetir o conteúdo de "Situações" no índice — daria dois
   * endereços para a mesma tela, e o item da navegação principal acenderia sem
   * que nenhuma aba ficasse marcada.
   */
  const indice = createRoute({
    getParentRoute: () => raiz,
    path: '/',
    // `href` e não `to`: este módulo é importado por `app/router.tsx`, então os
    // caminhos daqui ainda não existem no tipo do roteador quando ele é
    // compilado. É a mesma razão da conversão de parâmetro em
    // `features/tickets/routes.ts`.
    beforeLoad: () => {
      throw redirect({ href: `${RAIZ_DA_ADMINISTRACAO}/${CAMINHOS.situacoes}` });
    },
  });

  const aba = (caminho: string, component: () => JSX.Element) =>
    createRoute({ getParentRoute: () => raiz, path: caminho, component });

  return raiz.addChildren([
    indice,
    aba(CAMINHOS.situacoes, TicketStatusesPage),
    aba(CAMINHOS.categorias, TicketCategoriesPage),
    aba(CAMINHOS.subcategorias, TicketSubcategoriesPage),
    aba(CAMINHOS.equipes, TeamsPage),
    aba(CAMINHOS.organizacoes, OrganizationsPage),
    aba(CAMINHOS.notificacoes, NotificationsPage),
    aba(CAMINHOS.usuarios, UsuariosPage),
    aba(CAMINHOS.sla, SlaPage),
    aba(CAMINHOS.campos, TicketFieldDefinitionsPage),
    aba(CAMINHOS.email, EmailRoutingPage),
  ]);
}

export { CAMINHOS, RAIZ_DA_ADMINISTRACAO };
